import { type QConfigTableData } from '@exam-management/common/types';
import { QConfigStatus } from '@exam-management/graphql';
import {
  ExpandMoreRoundedIcon,
  Flex,
  IconButton,
  Rotate,
  styled,
  TableCell,
  TableRow,
  type TableRowProps,
} from '@shared/components';
import { useHasPermission } from '@shared/permissions';

import { EDIT_QCONFIG_REQUIRED_PERMISSIONS } from '../../constants';
import { StructureNameInput } from '../structure-name-input';

import { SubjectTableRowMenu } from './subject-table-row-menu';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderColor: theme.palette.grey[300],
  borderRight: `1px solid ${theme.palette.grey[300]}`,
  ...theme.typography.body2Semibold,
}));

function renderQuestionTypeCell(questionType: QConfigTableData['questionType']) {
  if (!questionType.single && !questionType.itemSet) {
    return '-';
  }

  return `${questionType.single ?? 0}/${questionType.itemSet ?? 0}`;
}

interface SubjectTableRowProps extends TableRowProps {
  data: QConfigTableData;
  expanded?: boolean;
  onExpand?: (id: string) => void;
  status?: QConfigStatus;
}

export default function SubjectTableRow(props: Readonly<SubjectTableRowProps>) {
  const { data, expanded: isExpanded = false, onExpand, status, ...restProps } = props;
  const { id, totalQuestions, questionType, scorePerQuestion, calculated, notCalculated } = data;
  const isDraftQConfig = status === QConfigStatus.Draft;
  const hasPermissionForEditingQConfig = useHasPermission(EDIT_QCONFIG_REQUIRED_PERMISSIONS);

  return (
    <TableRow
      {...restProps}
      sx={{
        border: 'none',
        ':last-child': {
          borderRight: 'none',
          td: {
            borderBottom: 'none',
          },
        },
        backgroundColor: isExpanded ? (theme) => theme.palette.primary.light : undefined,
        ':hover': { backgroundColor: (theme) => theme.palette.primary.light },
        ...restProps.sx,
      }}
    >
      <StyledTableCell
        sx={{
          p: (theme) => theme.spacing(0),
        }}
      >
        <Flex sx={{ alignItems: 'center' }}>
          <Flex sx={{ minWidth: '48px', alignItems: 'center', justifyContent: 'center' }}>
            <IconButton onClick={() => onExpand?.(id)}>
              <Rotate in={isExpanded}>
                <ExpandMoreRoundedIcon />
              </Rotate>
            </IconButton>
          </Flex>
          <Flex
            sx={(theme) => ({
              flex: 1,
              p: theme.spacing(2, 1),
            })}
          >
            <StructureNameInput
              name={id}
              variant='body2Semibold'
              disabled={!hasPermissionForEditingQConfig || !isDraftQConfig}
            />
          </Flex>
        </Flex>
      </StyledTableCell>
      <StyledTableCell align='center'>-</StyledTableCell>
      <StyledTableCell align='center'>{totalQuestions}</StyledTableCell>
      <StyledTableCell align='center'>{scorePerQuestion}</StyledTableCell>
      <StyledTableCell align='center'>{renderQuestionTypeCell(questionType)}</StyledTableCell>
      <StyledTableCell align='center'>{calculated.easy ?? '-'}</StyledTableCell>
      <StyledTableCell align='center'>{calculated.medium ?? '-'}</StyledTableCell>
      <StyledTableCell align='center'>{calculated.hard ?? '-'}</StyledTableCell>
      <StyledTableCell align='center'>{notCalculated.easy ?? '-'}</StyledTableCell>
      <StyledTableCell align='center'>{notCalculated.medium ?? '-'}</StyledTableCell>
      <StyledTableCell
        align='center'
        sx={{
          borderRight: !(isDraftQConfig && hasPermissionForEditingQConfig) ? 'none' : undefined,
        }}
      >
        {notCalculated.hard ?? '-'}
      </StyledTableCell>
      {isDraftQConfig && hasPermissionForEditingQConfig ? (
        <StyledTableCell
          align='center'
          sx={{
            borderRight: 'none',
          }}
        >
          <SubjectTableRowMenu subjectId={data.id} />
        </StyledTableCell>
      ) : null}
    </TableRow>
  );
}
